import { CartEdge } from '../../models/Cart';
import {
    CustomItem,
    QuotationCreateItem,
    QuotationCreateProps,
    QuotationCreateResp,
    QuotationDetailsProps,
    QuotationSummariesTableItem,
    QuotationSummaryProdTableItem, QuotationSummaryProdTableItemType, QuotationSummaryProdTableItemWithType,
    QuotationSummaryProps,
    QuotationUpdateProps,
    QuotationUserProps,
} from '../../models/quotations/Quotation';
import {
    QuotationData,
    QuotationProductItem,
    QuotationProductUpdatedItem,
    QuotationSummaryData,
    QuotationUpdateProductdItem,
    QuotationUserDetails,
} from '../../models/quotations/Quotations';
import httpApi from '../api/httpApi';
import useQuotationsSWR from './useQuotationsSWR';

const useQuotation = () => {
    const OFFER_URL = 'api/v1/offers';
    const BASE_DATA = OFFER_URL + '/base-data/';
    const SELLER_DATA = OFFER_URL + '/seller-data/';
    const BUYER_DATA = OFFER_URL + '/buyer-data/';
    const ITEMS_DATA = OFFER_URL + '/items/';
    const CUSTOM_ITEMS_DATA = OFFER_URL + '/custom-items/';
    const QUOTATION_ADD_ITEM = OFFER_URL + '/add-item-data/';
    const ADD_SECOND_COMMENT = OFFER_URL + '/add-second-comment/';
    const PDF = OFFER_URL + '/pdf';

    const { forceRefresh } = useQuotationsSWR();

    const createNewQuotation = (
        data: QuotationCreateProps
    ): Promise<QuotationCreateResp> => {
        return httpApi
            .post<{ offer: QuotationCreateResp }>(OFFER_URL, data)
            .then((resp) => {
                forceRefresh();
                return resp.getData().offer;
            });
    };

    const addProductsToQuotation = (
        props: QuotationUpdateProps
    ): Promise<QuotationData> => {
        return httpApi
            .post<{ offer: QuotationData }>(
                QUOTATION_ADD_ITEM + props.quotationId,
                props.products
            )
            .then((resp) => {
                forceRefresh();
                return resp.getData().offer;
            });
    };

    const convertProductsToQuotation = (
        data: CartEdge[]
    ): QuotationCreateItem[] => {
        return data.map((elem) => {
            return {
                sku: elem.node.merchandise.sku,
                quantity: elem.node.quantity,
            };
        });
    };
    const saveCustomItems = (
        data: CustomItem[],
        quoteId: number
    ): Promise<{ custom_items: CustomItem[] }> => {
        return httpApi
            .put<{ custom_items: CustomItem[] }>(CUSTOM_ITEMS_DATA + quoteId, {
                items: data,
            })
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const deleteQuotationItem = (quoteItemId: number): Promise<null> => {
        return httpApi.del<null>(ITEMS_DATA + quoteItemId).then((resp) => {
            return resp.getData();
        });
    };

    const saveDetails = (
        data: QuotationDetailsProps,
        quoteId: number
    ): Promise<QuotationCreateResp> => {
        return httpApi
            .put<{ offer: QuotationCreateResp }>(BASE_DATA + quoteId, data)
            .then((resp) => {
                forceRefresh();
                return resp.getData().offer;
            });
    };

    const saveSeller = (
        data: object,
        quoteId: number
    ): Promise<{ seller: QuotationUserProps }> => {
        return httpApi
            .put<{ seller: QuotationUserProps }>(SELLER_DATA + quoteId, data)
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const saveBuyer = (
        data: object,
        quoteId: number
    ): Promise<{ buyer: QuotationUserProps }> => {
        return httpApi
            .put<{ buyer: QuotationUserProps }>(BUYER_DATA + quoteId, data)
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const saveSummary = (
        data: QuotationSummaryProps,
        quoteId: number
    ): Promise<{ offer: QuotationCreateResp }> => {
        return httpApi
            .put<{ offer: QuotationCreateResp }>(BASE_DATA + quoteId, data)
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const savePDF = (
        quoteId: number,
        use_logo: number
    ): Promise<{ offer: QuotationCreateResp }> => {
        return httpApi
            .post<{ offer: QuotationCreateResp }>(PDF, {
                offer_id: quoteId,
                use_logo: use_logo,
            })
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const saveItems = (
        data: QuotationUpdateProductdItem[],
        quoteId: number
    ): Promise<{ items: QuotationProductUpdatedItem[] }> => {
        return httpApi
            .put<{ items: QuotationProductUpdatedItem[] }>(
                ITEMS_DATA + quoteId,
                {
                    items: data,
                }
            )
            .then((resp) => {
                forceRefresh();
                return resp.getData();
            });
    };

    const convertItemsToTable = (
        data: QuotationProductItem[]
    ): QuotationSummaryProdTableItemWithType[] => {
        return data.map((elem) => {
            return {
                type: QuotationSummaryProdTableItemType.PRODUCT,
                name: elem.name,
                image: elem.image,
                quantity: elem.quantity,
                net_unit_price: elem.net_price - elem.margin,
                net_price: elem.net_price * elem.quantity,
                tax: elem.tax,
                gross_price:
                    elem.net_price * elem.quantity * (elem.tax / 100 + 1),
                custom_items: elem.customs,
                price_margin: elem.margin,
                unit: elem.unit_measurement,
            };
        });
    };

    const convertCustomItemsToTable = (
        data: CustomItem[]
    ): QuotationSummaryProdTableItemWithType[] => {
        return data.map((elem) => {
            return {
                type: QuotationSummaryProdTableItemType.CUSTOM_ITEM,
                name: elem.name,
                image: 'icon',
                quantity: elem.quantity,
                net_unit_price: null,
                net_price: elem.net_price,
                tax: elem.tax,
                gross_price:
                    elem.net_price * elem.quantity * (elem.tax / 100 + 1),
                custom_items: [],
                price_margin: undefined,
                unit: elem.unit,
            };
        });
    };

    const getSummariesPrices = (
        data: QuotationSummaryData
    ): QuotationSummariesTableItem[] => {
        if (!data || !data.items_by_tax || !data.items_by_tax.summary) {
            return [];
        }
        const sumByTax = Object.keys(data?.items_by_tax.summary).map(
            (elem): QuotationSummariesTableItem => {
                return {
                    net_total: data.items_by_tax.summary[elem].net,
                    tax: Number(elem),
                    gross_total: data.items_by_tax.summary[elem].gross,
                };
            }
        );
        return [
            ...sumByTax,
            {
                net_total: data.net_total,
                tax: 0,
                gross_total: data.gross_total,
            },
        ];
    };

    const setUserDefaultValues = (
        userData: QuotationUserDetails
    ): QuotationUserProps => {
        return userData
            ? {
                  company: userData.company,
                  tax_number: userData.tax_number,
                  name: userData.name,
                  phone: userData.phone,
                  country: userData.country,
                  postcode: userData.postcode,
                  city: userData.city,
                  address1: userData.address1,
                  address2: userData.address2,
              }
            : null;
    };

    const getModifiedFields = (
        dirtyFields: object | boolean,
        allValues: object
    ): object => {
        if (dirtyFields === true || Array.isArray(dirtyFields))
            return allValues;
        return Object.fromEntries(
            Object.keys(dirtyFields).map((key) => [
                key,
                getModifiedFields(dirtyFields[key], allValues[key]),
            ])
        );
    };

    const removeEmptyLinesFromCustomItems = (
        quoteItems: CustomItem[]
    ): CustomItem[] => {
        return quoteItems.filter((elem) => elem.name && elem.name.length != 0);
    };

    return {
        createNewQuotation,
        convertProductsToQuotation,
        saveDetails,
        saveSeller,
        saveBuyer,
        saveItems,
        saveCustomItems,
        saveSummary,
        savePDF,
        convertItemsToTable,
        convertCustomItemsToTable,
        getSummariesPrices,
        setUserDefaultValues,
        getModifiedFields,
        addProductsToQuotation,
        removeEmptyLinesFromCustomItems,
        deleteQuotationItem,
    };
};

export default useQuotation;
